import {useState, useEffect} from 'react'
import * as Body from './Styles.'
import {Button} from '../../components/Button/Styles'
import * as functions from '../../utils/functions/functions'
import * as ModalsConfig from '../config/modals/Modals'
import Loading from '../../components/Loading/Loading'
import Modal from '../../components/Modal/Modal'
import TallyEmbed from '../../components/TallyEmbed/TallyEmbed'
import { Search, Globe, GlobeOff, ExternalLink, Flag } from 'lucide-react'

import warningIcon from '../../assets/icons/warning-icon.svg'
import checkIcon from '../../assets/icons/check-icon.svg'
import externalIcon from '../../assets/icons/external-icon.svg'

import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux'
import axios from 'axios'
import api from '../../utils/axiosInstance'
import { HomeState } from '../../utils/redux/types'
import { USE_MOCK } from '../../utils/mock/mockFlag'
import { getApiBaseUrl } from '../../utils/defines/apiUrl'
import { useIsMobile } from '../../utils/hooks/useIsMobile'
import { AutoProgressBar } from '../../components/AutoProgressBar/AutoProgressBar'

// ── Tipos ───────────────────────────────────────────────────────────

type AlarmeResumo = {
    local: string;
    descricao: string;
    cor: 'vermelho' | 'amarelo';
}

type MoendaProp = {
    token: string,
    nome: string,
    status: number,
    qtd_ternos: number,
    clp_status: number,
    nuvem_status: number,
    ativo: boolean,
    ultimo_envio: number,
    alarmes?: AlarmeResumo[]
}

// ── Mock de alarmes para visualização em desenvolvimento ────────────

const mockMoendas: MoendaProp[] = [
    {
        token: '',
        nome: 'Moenda — Alarmes (Mock)',
        status: 2,
        qtd_ternos: 3,
        clp_status: 1,
        nuvem_status: 1,
        ativo: true,
        ultimo_envio: Date.now() - 30_000,
        alarmes: [
            { local: 'Unidade Hidráulica', descricao: 'Temperatura acima da máxima', cor: 'vermelho' },
            { local: 'Terno 1', descricao: 'Diferença de pressão muito elevada', cor: 'vermelho' },
            { local: 'Terno 2', descricao: 'Carga inesperada no lado oposto', cor: 'amarelo' },
            { local: 'Terno 3', descricao: 'Vazamento no lado oposto', cor: 'amarelo' },
        ]
    },
    {
        token: '',
        nome: 'Moenda — CLP Error (Mock)',
        status: 2,
        qtd_ternos: 2,
        clp_status: 2,
        nuvem_status: 2,
        ativo: true,
        ultimo_envio: Date.now() - 2 * 3_600_000,
        alarmes: []
    },
    {
        token: '',
        nome: 'Moenda — Queda < 4h (Mock)',
        status: 2,
        qtd_ternos: 2,
        clp_status: 1,
        nuvem_status: 2,
        ativo: true,
        ultimo_envio: Date.now() - 2 * 3_600_000,
        alarmes: []
    },
    {
        token: '',
        nome: 'Moenda — Queda > 4h (Mock)',
        status: 2,
        qtd_ternos: 2,
        clp_status: 1,
        nuvem_status: 2,
        ativo: true,
        ultimo_envio: Date.now() - 6 * 3_600_000,
        alarmes: []
    },
    {
        token: '',
        nome: 'Moenda — Desativada (Mock)',
        status: 1,
        qtd_ternos: 2,
        clp_status: 1,
        nuvem_status: 1,
        ativo: false,
        ultimo_envio: Date.now() - 2 * 3_600_000,
        alarmes: []
    },
    {
        token: '',
        nome: 'Moenda OK (Mock)',
        status: 1,
        qtd_ternos: 2,
        clp_status: 1,
        nuvem_status: 1,
        ativo: true,
        ultimo_envio: Date.now() - 30_000,
        alarmes: []
    }
]

// ── Componente de resumo de alarmes ─────────────────────────────────

const Chip = ({ color, label }: { color: string; label: string }) => (
    <div style={{
        display: 'flex', alignItems: 'center', gap: '6px',
        background: `${color}1a`, borderRadius: '20px', padding: '3px 10px'
    }}>
        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: color, flexShrink: 0 }} />
        <span style={{ fontFamily: 'Nunito', fontSize: '13px', fontWeight: 400, color: 'rgba(255,255,255,0.85)' }}>
            {label}
        </span>
    </div>
)

const AlarmeSummary = ({ alarmes }: { alarmes: AlarmeResumo[] }) => {
    const criticos = alarmes.filter(a => a.cor === 'vermelho')
    const atencao  = alarmes.filter(a => a.cor === 'amarelo')

    if (!alarmes.length) return <Body.MoendaText>A moenda precisa de atenção</Body.MoendaText>

    return (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '2px' }}>
            {criticos.length > 0 && <Chip color="#F03F3F" label={`${criticos.length} crítico${criticos.length > 1 ? 's' : ''}`} />}
            {atencao.length > 0  && <Chip color="#E5D34A" label={`${atencao.length} em atenção`} />}
        </div>
    )
}

// ── Página principal ─────────────────────────────────────────────────

const Home = () => {

    const [data, setData] = useState<HomeState>()
    const [filtered, setFiltered] = useState<MoendaProp[]>(USE_MOCK ? mockMoendas : [])
    const [hasLength, toggleHasLength] = useState(false)
    const isMobile = useIsMobile()
    const [loading, toggleLoading] = useState(true)

    const history = useNavigate()
    const dispatch = useDispatch()

    const [addUser, toggleAddUser] = useState(false)
    const [editUsers, toggleEditUsers] = useState(false)
    const [changeEmail, toggleChangeEmail] = useState(false)
    const [changePassword, toggleChangePassword] = useState(false)
    const [alarmsReports, toggleAlarmsReports] = useState(false)
    const [gerarRelatorio, toggleGerarRelatorio] = useState(false)
    const [reportarProblema, toggleReportarProblema] = useState(false)

    const logout = () => {
        toggleLoading(true)
        axios.get('https://www.conecta.tectrolnet.com.br/api/Logout', { withCredentials: true })
            .then(() => {
                localStorage.setItem('@logged', '')
                history('/')
            })
            .catch((error) => {
                dispatch({ type: 'ADD_NOTIFICATION', status: 3, title: error.response.data.msg })
                toggleLoading(false)
            })
    }

    const fetchHome = async () => {
        if (USE_MOCK) {
            toggleLoading(false)
            return
        }
        try {
            const response = await api.get(`${getApiBaseUrl()}/moendas`)
            const user = response.data.usuario
            setData(response.data)
            setFiltered(response.data.moendas ?? [])
            dispatch({ type: 'SET_USER', nome: user.p_nome, sobrenome: user.s_nome })
        } catch (error: any) {
            functions.ResolveErrors(error.response.status, error.response.data.code, error.response.data.message, history, dispatch, error.response.data.content)
        } finally {
            toggleLoading(false)
        }
    }

    useEffect(() => {
        fetchHome()
    }, [])

    const filterData = (name: string) => {
        if (name.length === 0) {
            setFiltered(USE_MOCK ? mockMoendas : (data?.moendas ?? []))
            toggleHasLength(false)
            return
        }
        toggleHasLength(true)
        const source = USE_MOCK ? mockMoendas : (data?.moendas ?? [])
        setFiltered(source.filter(item => item.nome.toUpperCase().includes(name.toUpperCase())))
    }

    const getColor = (_item: MoendaProp) => '#FFF'

    const getCardSeverity = (item: MoendaProp): 'critico' | 'atencao' | 'ok' | 'inativa' => {
        if (!item.ativo) return 'inativa'
        if (item.clp_status === 2 || item.nuvem_status === 2) return 'critico'
        if ((item.alarmes ?? []).some(a => a.cor === 'vermelho')) return 'critico'
        if ((item.alarmes ?? []).some(a => a.cor === 'amarelo') || item.status === 2) return 'atencao'
        return 'ok'
    }

    const ConnectionIcon = ({ item }: { item: MoendaProp }) => {
        const disconnected = item.clp_status === 2 || item.nuvem_status === 2
        return disconnected ? <GlobeOff size={18} /> : <Globe size={18} />
    }

    const getTextConnection = (item: MoendaProp) => {
        if (!item.ativo) return 'Usina desativada'
        if (item.clp_status === 2) return 'Falha Obsidian ↔ CLP'
        if (item.nuvem_status === 2) return 'Sem conexão com a nuvem'
        return 'CLP conectado'
    }

    const getMoendaText = (item: MoendaProp) => {
        if (!item.ativo)
            return <Body.MoendaText>Usina desativada pela equipe DHM</Body.MoendaText>

        if (item.clp_status === 2)
            return <Body.MoendaText>Falha de comunicação entre o Obsidian e o CLP da usina. Os dados não estão sendo registrados.</Body.MoendaText>

        if (item.nuvem_status === 2) {
            const horas = (Date.now() - item.ultimo_envio) / 3_600_000
            if (horas < 4)
                return <Body.MoendaText>Queda de conexão recente (pode ser instabilidade na internet). Se o Obsidian estiver conectado ao CLP, os dados desse período não serão perdidos.</Body.MoendaText>
            return <Body.MoendaText>Sem conexão há {Math.floor(horas)}h. Verifique se o Obsidian está corretamente ligado na usina.</Body.MoendaText>
        }

        if (item.status === 1) return <Body.MoendaText>A moenda está funcionando normalmente</Body.MoendaText>
        return <AlarmeSummary alarmes={item.alarmes ?? []} />
    }

    return <Body.Container>
        <Body.Nav>
            <Body.NavTitleContent>
                <Body.NavTitle>DHM4.0</Body.NavTitle>
            </Body.NavTitleContent>
            <Body.NavButtons>
                {window.innerWidth > 1024 && (
                    <>
                    <Body.NavText>
                        Olá, {USE_MOCK ? 'Usuário Mock' : data?.usuario.p_nome}
                    </Body.NavText>
                    <Button $gradient onClick={() => window.open('/dashboard', 'dhm4-dashboard')}>
                        Ir para Dashboard
                    </Button>
                    </>
                )}
                <Button onClick={() => toggleReportarProblema(true)}>
                    <Flag size={16} />
                    {window.innerWidth > 1024 ? 'Enviar feedback' : null}
                </Button>
                <Button $gradient onClick={() => logout()}>
                    Sair
                </Button>
            </Body.NavButtons>
        </Body.Nav>

        {window.innerWidth <= 660 && (
            <Body.NavText>
                Olá, {USE_MOCK ? 'Usuário Mock' : data?.usuario.p_nome}
            </Body.NavText>
        )}

        <Body.Content>
            <Body.HalfContent percentage={59}>
                <Body.Card>
                    <Body.CardHeader>
                        <Body.Title>Moendas</Body.Title>
                        <div>
                            <Body.IconInput><Search size={16} /></Body.IconInput>
                            <Body.InputFind onChange={e => filterData(e.target.value)}
                                style={hasLength ? { width: isMobile ? '60vw' : '300px', maxWidth: isMobile ? 200 : undefined } : {}} />
                        </div>
                    </Body.CardHeader>
                    <Body.Scroll>
                        {/* Moendas ativas com problemas (status=2) primeiro */}
                        {filtered.map((item, index) =>
                            item.ativo && item.status === 2 ?
                            <Body.MoendaCard key={index.toString()} severity={getCardSeverity(item)}
                                onClick={() => history('/moenda?token=' + item.token + '&nome-usina=' + item.nome)}>
                                <Body.MoendaTextContent>
                                    <Body.MoendaTitle>{item.nome}</Body.MoendaTitle>
                                    {getMoendaText(item)}
                                </Body.MoendaTextContent>
                                <Body.Row>
                                    <Body.Circle color={getColor(item)} title={getTextConnection(item)}>
                                        <ConnectionIcon item={item} />
                                    </Body.Circle>
                                    <Body.Circle color={'#E5D34A'}>
                                        <Body.MoendaIcon src={warningIcon} />
                                    </Body.Circle>
                                </Body.Row>
                            </Body.MoendaCard> : null
                        )}
                        {/* Moendas ativas OK (status=1) depois */}
                        {filtered.map((item, index) =>
                            item.ativo && item.status === 1 ? <Body.MoendaCard key={index.toString()} severity={getCardSeverity(item)}
                                onClick={() => history('/moenda?token=' + item.token + '&nome-usina=' + item.nome)}>
                                <Body.MoendaTextContent>
                                    <Body.MoendaTitle>{item.nome}</Body.MoendaTitle>
                                    {getMoendaText(item)}
                                </Body.MoendaTextContent>
                                <Body.Row>
                                    <Body.Circle color={getColor(item)} title={getTextConnection(item)}>
                                        <ConnectionIcon item={item} />
                                    </Body.Circle>
                                    <Body.Circle color={'#92B258'}>
                                        <Body.MoendaIcon src={checkIcon} />
                                    </Body.Circle>
                                </Body.Row>
                            </Body.MoendaCard> : null
                        )}
                        {/* Moendas desativadas sempre por último, independente do status */}
                        {filtered.map((item, index) =>
                            !item.ativo ? <Body.MoendaCard key={index.toString()} severity={getCardSeverity(item)}
                                onClick={() => history('/moenda?token=' + item.token + '&nome-usina=' + item.nome)}>
                                <Body.MoendaTextContent>
                                    <Body.MoendaTitle>{item.nome}</Body.MoendaTitle>
                                    {getMoendaText(item)}
                                </Body.MoendaTextContent>
                                <Body.Row>
                                    <Body.Circle color={getColor(item)} title={getTextConnection(item)}>
                                        <ConnectionIcon item={item} />
                                    </Body.Circle>
                                    <Body.Circle color={'#92B258'}>
                                        <Body.MoendaIcon src={checkIcon} />
                                    </Body.Circle>
                                </Body.Row>
                            </Body.MoendaCard> : null
                        )}
                    </Body.Scroll>
                </Body.Card>
            </Body.HalfContent>
            <Body.HalfContent percentage={40}>
                <Body.Card style={{ height: 'auto' }}>
                    <Body.Title style={{ textAlign: 'start', alignSelf: 'flex-start'}}>Gerenciar Conta</Body.Title>
                    <Body.MoendaText style={{ textAlign: 'start', marginTop: 12 }}>
                        Acesse a Plataforma Conecta para gerenciar sua conta, redefinir sua senha e visualizar informações sobre usuários e aplicativos da sua empresa.
                    </Body.MoendaText>
                    <Body.ButtonContent style={{ marginTop: 12 }}>
                        <Button onClick={() => window.open('https://www.conecta.tectrolnet.com.br/', '_blank')}>
                            {window.innerWidth > 1024? "Ir para a Plataforma Conecta":"Ir para o Conecta" }
                            <ExternalLink/>
                        </Button>
                    </Body.ButtonContent>
                </Body.Card>
                <Body.Card style={{ height: 'auto', marginTop: 12 }}>
                    <Body.Title style={{ textAlign: 'start', alignSelf: 'flex-start'}}>Alarmes e Relatórios</Body.Title>
                    <Body.MoendaText style={{ textAlign: 'start', marginTop: 12  }}>
                        Configure o recebimento de alertas e relatórios por e-mail e navegador, personalize os horários das notificações, ou gere um relatório imediatamente.
                    </Body.MoendaText>
                    <Body.ButtonContent style={{ marginTop: 12, width: '100%', justifyContent: 'flex-start', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 10 : 15 }}>
                        <Button onClick={() => toggleAlarmsReports(true)} style={isMobile ? { width: '100%', height: 40 } : { flex: 1, width: '100%' }}>
                            Configurar
                        </Button>
                        <Button onClick={() => toggleGerarRelatorio(true)} style={isMobile ? { width: '100%', height: 40 } : { flex: 1, width: '100%' }}>
                            Gerar relatório
                        </Button>
                    </Body.ButtonContent>
                </Body.Card>
            </Body.HalfContent>
        </Body.Content>
        {addUser ? <ModalsConfig.RegisterUser toggle={toggleAddUser} /> : null}
        {editUsers ? <ModalsConfig.EditUsers toggleModal={toggleEditUsers} /> : null}
        {changeEmail ? <ModalsConfig.ChangeEmail toggle={toggleChangeEmail} /> : null}
        {changePassword ? <ModalsConfig.ChangePassword toggle={toggleChangePassword} /> : null}
        {alarmsReports ? <ModalsConfig.AlarmsReports toggle={toggleAlarmsReports} loading={loading} setLoading={toggleLoading} /> : null}
        {gerarRelatorio ? <ModalsConfig.GerarRelatorio toggle={toggleGerarRelatorio} /> : null}
        {reportarProblema ? (
            <Modal toggleModal={toggleReportarProblema} title="Enviar feedback" >
                <TallyEmbed
                    src="https://tally.so/embed/rjRMrN?alignLeft=1&hideTitle=1&transparentBackground=1"
                    title="Feedback DHM4.0 – Sugestões e Erros"
                    width={isMobile ? undefined : 700}
                    height={800}
                />
            </Modal>
        ) : null}
        {loading ? <Loading /> : null}
        {!isMobile && <AutoProgressBar onComplete={fetchHome} />}
    </Body.Container>
}

export default Home
